import type { ReasoningStepData } from '@/components/reasoning/ReasoningStepsList';
import { ThinkingIndicator } from '@/components/reasoning/ThinkingIndicator';
import { InlineEditProvider } from '@/contexts/InlineEditContext';
import type { FeedbackRating } from '@/lib/reasoning/feedback';

import { ChainTurn } from './ChainTurn';
import type { ChatChain } from './types';
import { UserMessageBubble } from './UserMessageBubble';

interface ChatConversationThreadProps {
  orgId: number;
  chains: ChatChain[];
  /** Shown before the first chain loads (navigating in from the home composer). */
  optimisticMessage: string | null;
  /** Something is loading or running with no chain to hang it on yet. */
  isAwaitingFirstChain: boolean;
  isPolling: boolean;
  hasActiveTask: boolean;
  progressStatus: string;
  partialSteps: ReasoningStepData[];
  regeneratingChainId: string | null;
  chainFeedback: Record<string, FeedbackRating | null>;
  showInlineProposals: boolean;
  isBuilderConversation: boolean;
  shownProposalId: number | null | undefined;
  onSelectProposal: (proposalId: number) => void;
  onOpenDynamicViewSidebar: (viewId: number) => void;
  onOpenReasoningSidebar: (chainId: string) => void;
  onRetry: (chainId: string) => void;
  onFeedback: (chainId: string, rating: FeedbackRating) => void;
}

/** The conversation tab: every chain turn, plus pre-first-chain placeholders. */
export function ChatConversationThread({
  orgId,
  chains,
  optimisticMessage,
  isAwaitingFirstChain,
  progressStatus,
  partialSteps,
  chainFeedback,
  onOpenDynamicViewSidebar,
  ...turnProps
}: ChatConversationThreadProps) {
  const hasChains = chains.length > 0;

  return (
    <InlineEditProvider>
      <div className='max-w-3xl w-full mx-auto space-y-6 pl-[27px] pr-[17px] sm:pl-[31px] sm:pr-[21px] lg:pl-[39px] lg:pr-[29px] pt-2.5 min-w-0 overflow-x-clip'>
        {optimisticMessage && !hasChains && (
          <div className='space-y-4'>
            <UserMessageBubble text={optimisticMessage} orgId={orgId} />
            <div className='flex gap-2 sm:gap-3 items-start mb-6'>
              <div className='flex-1 min-w-0 space-y-1.5'>
                <ThinkingIndicator
                  progressStatus={progressStatus}
                  partialSteps={partialSteps}
                  onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
                />
              </div>
            </div>
          </div>
        )}

        {chains.map((chain, index) => (
          <ChainTurn
            key={chain.id}
            chain={chain}
            orgId={orgId}
            isLastChain={index === chains.length - 1}
            feedback={chainFeedback[chain.id]}
            progressStatus={progressStatus}
            partialSteps={partialSteps}
            onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
            {...turnProps}
          />
        ))}

        {!hasChains && !optimisticMessage && isAwaitingFirstChain && (
          <ThinkingIndicator
            progressStatus={progressStatus}
            partialSteps={partialSteps}
            onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
          />
        )}
      </div>
    </InlineEditProvider>
  );
}
